import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { api, setOnUnauthorized, tokenStore, type AdminMe } from './api';

interface Ctx {
  me: AdminMe;
  reload: () => void;
  can: (p: string) => boolean;
  logout: () => Promise<void>;
}
const AdminContext = createContext<Ctx | null>(null);

export function useAdmin() {
  const c = useContext(AdminContext);
  if (!c) throw new Error('useAdmin outside provider');
  return c;
}

/**
 * Права используются только для скрытия недоступных элементов интерфейса.
 * Реальная проверка каждого действия — на сервере (AdminGuard).
 */
export function AdminProvider({ children, login }: { children: ReactNode; login: (onDone: () => void) => ReactNode }) {
  const [me, setMe] = useState<AdminMe | null>(null);
  const [checked, setChecked] = useState(false);
  const load = () => {
    if (!tokenStore.get()) {
      setChecked(true);
      return;
    }
    api<AdminMe>('/admin/auth/me')
      .then(setMe)
      .catch(() => tokenStore.set(null))
      .finally(() => setChecked(true));
  };
  useEffect(() => {
    setOnUnauthorized(() => {
      tokenStore.set(null);
      setMe(null);
    });
    load();
    // Права роли и политика 2FA могут измениться, пока панель открыта: перечитываем раз в минуту
    // и при возвращении на вкладку (иначе меню обновилось бы только после перезагрузки страницы).
    const refresh = () => {
      if (tokenStore.get() && document.visibilityState === 'visible') api<AdminMe>('/admin/auth/me').then(setMe).catch(() => undefined);
    };
    const timer = setInterval(refresh, 60_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);
  if (!checked) return null;
  if (!me) return <>{login(load)}</>;
  const value: Ctx = {
    me,
    reload: load,
    can: (p) => me.permissions.includes(p),
    logout: async () => {
      await api('/admin/auth/logout', { method: 'POST' }).catch(() => undefined);
      tokenStore.set(null);
      setMe(null);
    },
  };
  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}
